import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, DatePicker, Select, Button, Tag } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { toast } from 'react-toastify';
import apiService from '../../services/ApiService';
import { usePromotionEntryColumns, STATUS_LABELS, TYPE_LABELS } from './PromotionColumns';
import {
    fetchWinnerEntryMap,
    getEntryRowClassName,
    loadEntriesListState,
    saveEntriesListState,
    clearEntriesListState,
    DEFAULT_ENTRY_FILTERS,
    getPromotionStartDay,
} from './PromotionHelpers';
import '../Quiz/quizMain.css';

dayjs.extend(customParseFormat);

function normalizePhoneFilter(phone) {
    if (typeof phone !== 'string') return phone;
    return phone.trim().replace(/^\++/, '');
}

function buildQuery(params) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        const normalizedValue = key === 'phone' ? normalizePhoneFilter(value) : value;
        if (normalizedValue !== undefined && normalizedValue !== null && normalizedValue !== '') {
            search.append(key, normalizedValue);
        }
    });
    const query = search.toString();
    return query ? `?${query}` : '';
}

const PRIZE_TYPE_LABELS = {
    beko: 'Beko',
    dtl: 'DTL',
    main_clio: 'Clio',
};

const PromotionEntries = () => {
    const savedState = useRef(loadEntriesListState()).current;
    const { columns, handleRowClick } = usePromotionEntryColumns();

    const [entries, setEntries] = useState([]);
    const [winnerEntryMap, setWinnerEntryMap] = useState(new Map());
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: savedState?.pagination?.page ?? 1,
        limit: savedState?.pagination?.limit ?? 50,
        total: 0,
    });

    const [filters, setFilters] = useState(savedState?.filters ?? DEFAULT_ENTRY_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(
        savedState?.appliedFilters ?? savedState?.filters ?? DEFAULT_ENTRY_FILTERS
    );

    const fetchEntries = useCallback(async (page, limit, currentFilters) => {
        setLoading(true);

        const query = buildQuery({
            page,
            limit,
            email: currentFilters.email,
            phone: currentFilters.phone,
            pfr: currentFilters.pfr,
            status: currentFilters.status,
            type: currentFilters.type,
            dateFrom: currentFilters.dateFrom,
            dateTo: currentFilters.dateTo,
        });

        const [[success, error], winnerMap] = await Promise.all([
            apiService.get(`promotion/entries${query}`),
            fetchWinnerEntryMap(),
        ]);

        setLoading(false);

        if (error) {
            toast.error('Doslo je do greske, pokusajte malo kasnije');
            return;
        }

        const data = success?.data;
        setEntries(data?.entries || []);
        setWinnerEntryMap(winnerMap);
        setPagination({
            page: data?.pagination?.page || page,
            limit: data?.pagination?.limit || limit,
            total: data?.pagination?.total || 0,
        });
    }, []);

    useEffect(() => {
        fetchEntries(pagination.page, pagination.limit, appliedFilters);
    }, [appliedFilters, pagination.page, pagination.limit, fetchEntries]);

    useEffect(() => {
        saveEntriesListState({ filters, appliedFilters, pagination });
    }, [filters, appliedFilters, pagination]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const promotionStart = getPromotionStartDay();

    const disableDateFrom = (current) => {
        if (!current || !promotionStart) return false;
        return current.startOf('day').isBefore(promotionStart);
    };

    const disableDateTo = (current) => {
        if (!current) return false;

        const currentDay = current.startOf('day');

        if (promotionStart && currentDay.isBefore(promotionStart)) {
            return true;
        }

        if (filters.dateFrom) {
            const fromDay = dayjs(filters.dateFrom, 'YYYY-MM-DD').startOf('day');
            return currentDay.isBefore(fromDay);
        }

        return false;
    };

    const handleDateChange = (field, date) => {
        const formatted = date ? dayjs(date).format('YYYY-MM-DD') : null;

        if (field === 'dateFrom') {
            setFilters((prev) => {
                const next = { ...prev, dateFrom: formatted };

                if (
                    formatted &&
                    prev.dateTo &&
                    dayjs(prev.dateTo, 'YYYY-MM-DD').isBefore(dayjs(formatted, 'YYYY-MM-DD'))
                ) {
                    next.dateTo = formatted;
                }

                return next;
            });
            return;
        }

        handleFilterChange(field, formatted);
    };

    const applyFilters = () => {
        setPagination((prev) => ({ ...prev, page: 1 }));
        setAppliedFilters({ ...filters });
    };

    const resetFilters = () => {
        setFilters(DEFAULT_ENTRY_FILTERS);
        setAppliedFilters(DEFAULT_ENTRY_FILTERS);
        setPagination((prev) => ({ ...prev, page: 1 }));
        clearEntriesListState();
    };

    const handleTableChange = (tablePagination) => {
        setPagination((prev) => ({
            ...prev,
            page: tablePagination.current,
            limit: tablePagination.pageSize,
        }));
    };

    const columnsWithWinner = [
        ...columns.slice(0, 3),
        {
            title: 'Dobitnik',
            key: 'winner',
            render: (_, record) => {
                const winnerInfo = winnerEntryMap.get(record.id);
                if (!winnerInfo) return '-';

                return (
                    <Tag color={winnerInfo.isReserve ? 'gold' : 'green'}>
                        {winnerInfo.isReserve ? 'Rezerva' : 'Dobitnik'}
                        {winnerInfo.prizeType
                            ? ` · ${PRIZE_TYPE_LABELS[winnerInfo.prizeType] || winnerInfo.prizeType}`
                            : ''}
                    </Tag>
                );
            },
        },
        ...columns.slice(3),
    ];

    return (
        <div className="quizMain">
            <div className="title">NI prijave</div>

            <div className="filters">
                <div className="holder">
                    <label htmlFor="dateFrom">Datum od:</label>
                    <DatePicker
                        id="dateFrom"
                        value={filters.dateFrom ? dayjs(filters.dateFrom, 'YYYY-MM-DD') : null}
                        onChange={(date) => handleDateChange('dateFrom', date)}
                        disabledDate={disableDateFrom}
                        placeholder="YYYY-MM-DD"
                        className="timePicker"
                    />
                </div>

                <div className="holder">
                    <label htmlFor="dateTo">Datum do:</label>
                    <DatePicker
                        id="dateTo"
                        value={filters.dateTo ? dayjs(filters.dateTo, 'YYYY-MM-DD') : null}
                        onChange={(date) => handleDateChange('dateTo', date)}
                        disabledDate={disableDateTo}
                        placeholder="YYYY-MM-DD"
                        className="timePicker"
                    />
                </div>

                <div className="holder">
                    <label htmlFor="emailFilter">Email</label>
                    <input
                        id="emailFilter"
                        type="text"
                        value={filters.email}
                        onChange={(e) => handleFilterChange('email', e.target.value)}
                    />
                </div>

                <div className="holder">
                    <label htmlFor="phoneFilter">Telefon</label>
                    <input
                        id="phoneFilter"
                        type="text"
                        value={filters.phone}
                        onChange={(e) => handleFilterChange('phone', e.target.value)}
                    />
                </div>

                <div className="holder">
                    <label htmlFor="pfrFilter">PFR</label>
                    <input
                        id="pfrFilter"
                        type="text"
                        value={filters.pfr}
                        onChange={(e) => handleFilterChange('pfr', e.target.value)}
                    />
                </div>

                <div className="holder">
                    <label htmlFor="statusFilter">Status</label>
                    <Select
                        id="statusFilter"
                        allowClear
                        placeholder="Svi"
                        style={{ width: 160 }}
                        value={filters.status}
                        onChange={(value) => handleFilterChange('status', value)}
                        options={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))}
                    />
                </div>

                <div className="holder">
                    <label htmlFor="typeFilter">Tip</label>
                    <Select
                        id="typeFilter"
                        allowClear
                        placeholder="Svi"
                        style={{ width: 160 }}
                        value={filters.type}
                        onChange={(value) => handleFilterChange('type', value)}
                        options={Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }))}
                    />
                </div>

                <div className="holder">
                    <label>&nbsp;</label>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Button type="primary" onClick={applyFilters}>
                            Primeni
                        </Button>
                        <Button onClick={resetFilters}>Reset</Button>
                    </div>
                </div>
            </div>

            <Table
                rowKey="id"
                dataSource={entries}
                columns={columnsWithWinner}
                bordered
                loading={loading}
                rowClassName={(record) => getEntryRowClassName(record.id, winnerEntryMap)}
                onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                    style: { cursor: 'pointer' },
                })}
                pagination={{
                    current: pagination.page,
                    pageSize: pagination.limit,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                }}
                onChange={handleTableChange}
            />
        </div>
    );
};

export default PromotionEntries;
